import type { Brand } from "../../brands";
import type { DynamicStructure } from "../../interpolate/types";
import { getNodeDynamicData } from "../../node/getNodeDynamicData";
import { BLOCK_STRUCTURED_DATA_BY_TYPE } from "../blocks";
import type { StructuredDataBlockSource } from "../types";

interface CollectBlockSourcesParams {
  /** Árvore da página, no formato serializado do Craft.js. */
  pageData?: Record<string, any> | null;
  /** Entry da página de detalhe. Ausente nas demais rotas. */
  dynamicPageContent?: Record<string, any> | null;
  /** Resultado já filtrado, ordenado e paginado de cada bloco dinâmico. */
  dynamicContentsData?: Record<string, any> | null;
  channelData?: Record<string, any> | null;
  brands?: Brand[];
}

function typeNameOf(node: any): string {
  const type = node?.type;

  if (typeof type === "string") return type;
  if (type && typeof type === "object") return type.resolvedName ?? "";

  return "";
}

/**
 * Blocos da página que sabem contribuir com o grafo.
 *
 * Percorre a árvore como o `resolveListingLinks` já faz e fica só com os nós
 * que têm resolver registrado — o contexto não carrega a página inteira.
 *
 * O `structure` de cada nó é montado igual ao do `Node.tsx`, incluindo o
 * `section` do próprio bloco: a interpolação da marcação precisa enxergar
 * exatamente o que a interpolação do render enxerga, senão o JSON-LD descreve
 * uma trilha que não está na tela.
 */
export function collectBlockSources({
  pageData,
  dynamicPageContent,
  dynamicContentsData,
  channelData,
  brands,
}: CollectBlockSourcesParams): StructuredDataBlockSource[] {
  const sources: StructuredDataBlockSource[] = [];

  Object.entries(pageData ?? {}).forEach(([nodeId, node]) => {
    const typeName = typeNameOf(node);
    if (!BLOCK_STRUCTURED_DATA_BY_TYPE[typeName]) return;

    const structure: DynamicStructure = {
      item: dynamicPageContent ?? undefined,
      channel: channelData ?? undefined,
      section: getNodeDynamicData({
        nodeId,
        data: dynamicContentsData ?? undefined,
      }),
      brands,
    };

    sources.push({
      nodeId,
      typeName,
      props: (node as any)?.props ?? {},
      structure,
    });
  });

  return sources;
}
